import type {
  ErrorResponse,
  MultiPayment,
  MultiPaymentElementChangeEvent,
  MultiPaymentElementOptions,
  MultiPaymentElements,
  TokenObj,
  TokenResponse,
} from "@mul-pay/mptoken-js";

const TOKEN_NUMBER = "1";
const CARD_TOKEN_TIMEOUT_MS = 50000;

export type CardTokenInput = {
  multiPayment: MultiPayment | null;
  elements: MultiPaymentElements | null;
  holderName: string;
};

export type CardElementType = MultiPaymentElementChangeEvent["elementType"];

export type CardElementStatus = {
  complete: boolean;
  errorMessage?: string;
};

type CardTokenTenantSlug = "demo" | "miyagi";

const cardTokenTenantConfigs: Record<
  CardTokenTenantSlug,
  {
    apiKeyEnvName: string;
    pubKeyEnvName: string;
    getApiKey: () => string;
    getPubKey: () => string;
  }
> = {
  demo: {
    apiKeyEnvName: "VITE_DEMO_TENANT_MPTOKEN_API_KEY",
    pubKeyEnvName: "VITE_DEMO_TENANT_MPTOKEN_PUBLIC_KEY",
    getApiKey: () => import.meta.env.VITE_DEMO_TENANT_MPTOKEN_API_KEY ?? "",
    getPubKey: () => import.meta.env.VITE_DEMO_TENANT_MPTOKEN_PUBLIC_KEY ?? "",
  },
  miyagi: {
    apiKeyEnvName: "VITE_MIYAGI_TENANT_MPTOKEN_API_KEY",
    pubKeyEnvName: "VITE_MIYAGI_TENANT_MPTOKEN_PUBLIC_KEY",
    getApiKey: () => import.meta.env.VITE_MIYAGI_TENANT_MPTOKEN_API_KEY ?? "",
    getPubKey: () =>
      import.meta.env.VITE_MIYAGI_TENANT_MPTOKEN_PUBLIC_KEY ?? "",
  },
};

export const initialCardElementStatuses: Record<
  CardElementType,
  CardElementStatus
> = {
  cardNumber: { complete: false },
  cardExpiry: { complete: false },
  cardCvc: { complete: false },
};

export const cardElementOptions = {
  style: {
    base: {
      color: "#0F172A",
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      lineHeight: "24px",
      "::placeholder": {
        color: "#94A3B8",
      },
    },
    invalid: {
      color: "#B42318",
    },
  },
} satisfies MultiPaymentElementOptions;

export async function loadMulpayForCardToken(tenantSlug: string) {
  const config = getCardTokenConfig(tenantSlug);

  if (!config.apiKey) {
    throw new Error(`${config.apiKeyEnvName} is not configured`);
  }
  if (!config.pubKey) {
    throw new Error(`${config.pubKeyEnvName} is not configured`);
  }

  const { loadMulpay } = await import("@mul-pay/mptoken-js");

  return loadMulpay(
    config.apiKey,
    config.pubKey,
    undefined,
    true,
    import.meta.env.VITE_MULPAY_PRODUCTION_MODE === "true",
  );
}

function getCardTokenConfig(tenantSlug: string): {
  apiKey: string;
  pubKey: string;
  apiKeyEnvName: string;
  pubKeyEnvName: string;
} {
  if (!isCardTokenTenantSlug(tenantSlug)) {
    throw new Error(`MULPAY_CARD_TOKEN_UNSUPPORTED_TENANT_${tenantSlug}`);
  }

  const config = cardTokenTenantConfigs[tenantSlug];

  return {
    apiKey: config.getApiKey(),
    pubKey: config.getPubKey(),
    apiKeyEnvName: config.apiKeyEnvName,
    pubKeyEnvName: config.pubKeyEnvName,
  };
}

function isCardTokenTenantSlug(
  tenantSlug: string,
): tenantSlug is CardTokenTenantSlug {
  return tenantSlug in cardTokenTenantConfigs;
}

export async function createCardToken(input: CardTokenInput) {
  const { multiPayment, elements, holderName } = input;

  if (!multiPayment || !elements) {
    throw new Error("GMOPG_ELEMENTS_NOT_READY");
  }

  const cardElement = elements.getElement("cardNumber");
  if (!cardElement) {
    throw new Error("GMOPG_CARD_ELEMENT_NOT_READY");
  }

  const tokenResponse = await withTimeout(
    multiPayment.getTokenThroughIframe(
      cardElement,
      holderName.trim().toUpperCase(),
      { tokenNumber: TOKEN_NUMBER },
    ),
    CARD_TOKEN_TIMEOUT_MS,
    "GMOPG_TOKEN_TIMEOUT",
  );

  if (!isTokenSuccess(tokenResponse)) {
    throw new Error(formatTokenError(tokenResponse));
  }

  const token = tokenResponse.tokenList[0];
  if (token) {
    return token;
  }

  throw new Error("GMOPG_TOKEN_EMPTY");
}

function isTokenSuccess(response: TokenResponse): response is TokenObj {
  return response.result === "success";
}

function formatTokenError(response: ErrorResponse) {
  const legacyCodes = (response.errorList ?? [])
    .map((error) => error.legacyCode)
    .filter(Boolean);

  if (legacyCodes.length > 0) {
    return `GMOPG_TOKEN_${legacyCodes.join("_")}`;
  }

  return "GMOPG_TOKEN_FAILED";
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string,
) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  });
}
